import React, { useEffect, useMemo, useState } from 'react';
import { deriveInitialConsultationMode } from 'utils/consultationView';
import { AbilityProvider, defineAbility } from 'shared/access';
import { TierCtx, type TierValue } from 'shared/state/tierContext';
import { useVendorCtx } from 'shared/state/configContexts';
import { usePermissions } from './PermissionsProvider';

export function TierProvider({ children }: { children: React.ReactNode }) {
  const { userGroups, isCheckingAccess, canToggleConsultation } = usePermissions();
  const { vendor } = useVendorCtx();
  const forced = vendor.businessRules.forceConsultationMode === true;

  // Same seeding as StorageConfiguratorApp: after permissions resolve, and again
  // whenever the vendor changes (a forced vendor pins it on).
  const [consultationMode, setConsultationMode] = useState(true);
  useEffect(() => {
    if (isCheckingAccess) return;
    setConsultationMode(deriveInitialConsultationMode(canToggleConsultation, forced));
  }, [isCheckingAccess, canToggleConsultation, forced, vendor.id]);

  const ability = useMemo(() => defineAbility({ groups: userGroups, vendor, consultationMode }), [userGroups, vendor, consultationMode]);

  const value = useMemo<TierValue>(() => ({
    tier: ability.tier, roleLabel: ability.roleLabel,
    consultationMode, setConsultationMode, forcedConsultation: forced, canToggle: ability.flags.canToggleConsultation,
  }), [ability, consultationMode, forced]);

  return (
    <AbilityProvider value={ability}>
      <TierCtx.Provider value={value}>{children}</TierCtx.Provider>
    </AbilityProvider>
  );
}
